import { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { Header } from "../../../components/Header";
import { Loader } from "../../../components/Loader";
import { useAuthContext } from "../../../context/auth";
import { getRateForOpp } from "../../../utils/calculateVentas";
import { cargarOportunidadesVentas } from "../../../utils/cargarOportunidadesVentas";
import { fetchResumenGrupos } from "../../../utils/getActividadPrecalculada";
import { getEmpresasVip } from "../../../utils/getEmpresasVip";
import { useCurrency } from "../../../utils/useCurrency";
import { useExchangeRates } from "../../../utils/useExchangeRates";
import { useHubHeaderFilters } from "../HubHeaderContext";
import { useVentasDataSync } from "../VentasDataVersionContext";
import { construirDatosCategorias } from "./construirDatosCategorias";
import { ListaEmpresasModal } from "./ListaEmpresasModal";
import { PERIODOS, etiquetaMes, etiquetaMesCorta, getMesesDelPeriodo } from "./periodoUtil";
import styles from "./Categorias.module.scss";

const DATA_VACIA = { oportunidades: [], clientes: [], categorias: [], subcategorias: [] };

// Escala de UN SOLO verde, claro a oscuro, 5 pasos (2026-09-23, pedido explícito de Ignacio:
// "estamos hablando de ventas" — nunca amarillo/azul/rojo acá, esos colores ya significan estatus
// en el resto del hub (la Pizarra los usa para 50/75/90/100%); el verde más oscuro es el MISMO
// #198754 que usa "Activado" en el panel de cobertura — en esta pantalla el verde significa
// ventas en los dos paneles, a propósito. Sin números adentro de las celdas: el monto va en el
// tooltip.
const ESCALA_VERDE = ["#e8f3ed", "#c6e2d3", "#8fc7ab", "#4f9e78", "#198754"];
const COLOR_VACIO = "#f8f9fa";

const colorDeCelda = (monto, maximo) => {
  if (!monto || monto <= 0) return COLOR_VACIO;
  if (maximo <= 0) return ESCALA_VERDE[0];
  const ratio = monto / maximo;
  const paso = Math.min(ESCALA_VERDE.length - 1, Math.floor(ratio * ESCALA_VERDE.length));
  return ESCALA_VERDE[paso];
};

// Umbral de "actividad de CRM en los últimos 90 días" (VINCULADO, panel de cobertura) — leído
// directo de la tabla precalculada (crm_actividad_resumen_grupos.ultima_fecha_del_grupo), sin
// recalcular nada del camino de origen (Gmail/Calendar). Un cliente puede tener varias filas
// (lado/tipo/día); alcanza con la más reciente de todas.
const DIAS_VINCULADO = 90;
const construirClienteIdsConActividadReciente = (filasResumen) => {
  const maxPorCliente = new Map();
  (filasResumen || []).forEach((f) => {
    const t = new Date(f.ultima_fecha_del_grupo).getTime();
    if (isNaN(t)) return;
    const prev = maxPorCliente.get(f.cliente_id) || 0;
    if (t > prev) maxPorCliente.set(f.cliente_id, t);
  });
  const limite = DIAS_VINCULADO * 24 * 60 * 60 * 1000;
  const ahora = Date.now();
  const set = new Set();
  maxPorCliente.forEach((t, clienteId) => {
    if (ahora - t <= limite) set.add(clienteId);
  });
  return set;
};

/**
 * Vista Categorías — 6ª tab del hub de Ventas: mapa de calor por categoría × mes de cierre
 * (arriba) y cobertura ACTIVADO/VINCULADO/SIN CONTACTO por categoría (abajo). Unidad = EMPRESA,
 * nunca fila de `clientes` (granulada por marca) — ver docs/decisiones/2026-09-23-categorias-*.
 * Ver docs/modulos/categorias-ventas.md.
 */
const Categorias = ({ renderHeader, tabKey, active } = {}) => {
  const { pais } = useAuthContext();
  const { formatAmount } = useCurrency();
  const { getDolarValue, dolarFallback } = useExchangeRates(pais);
  const fallbackRate = dolarFallback?.[pais] || 1;

  const [periodo, setPeriodo] = useState(PERIODOS.ANO_ACTUAL);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(DATA_VACIA);
  const [empresasVipSet, setEmpresasVipSet] = useState(new Set());
  const [clienteIdsConActividadReciente, setClienteIdsConActividadReciente] = useState(new Set());
  const [modalTramo, setModalTramo] = useState(null); // { categoriaLabel, estado, nombres } | null

  // Categorías es SOLO LECTORA de oportunidades — nunca escribe estatus/elimina, así que solo
  // necesita el lado consumidor del contrato del hub (nunca llama bump()).
  const { stale: datosDesactualizados, marcarSincronizada } = useVentasDataSync(!!active);

  const cargarTodo = useCallback(() => {
    setLoading(true);
    return Promise.all([cargarOportunidadesVentas({ pais }), getEmpresasVip(pais)])
      .then(async ([oportunidadesData, vipSet]) => {
        const clienteIds = (oportunidadesData.clientes || []).map((c) => c.id);
        const filasResumen = await fetchResumenGrupos(clienteIds);
        setData(oportunidadesData);
        setEmpresasVipSet(vipSet);
        setClienteIdsConActividadReciente(construirClienteIdsConActividadReciente(filasResumen));
        marcarSincronizada();
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error("Error cargando Categorías:", error);
        toast.error("Error al cargar datos");
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pais]);

  useEffect(() => {
    let cancelled = false;
    cargarTodo().then(() => {
      if (cancelled) return;
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pais]);

  // Recarga silenciosa si otra tab del hub escribió una oportunidad mientras Categorías estaba
  // oculta o activa (mismo contrato que Pizarra/Detalle) — sin pasar por el loader de página.
  useEffect(() => {
    if (!datosDesactualizados) return undefined;
    let cancelled = false;
    cargarTodo().then(() => {
      if (cancelled) return;
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datosDesactualizados]);

  const getImporteLocal = useCallback(
    (op) => {
      const importe = op.type === "btl" ? op.total_investment || 0 : parseFloat(op.importe) || 0;
      if (!op.moneda_dolar) return importe;
      const rate = getRateForOpp(op, getDolarValue, fallbackRate);
      return importe * rate;
    },
    [getDolarValue, fallbackRate]
  );

  const formatMonedaLocal = useCallback(
    (valor) => {
      if (valor === null || valor === undefined || isNaN(valor)) return "-";
      if (pais === "Perú") return formatAmount(valor, "PEN", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
      return formatAmount(valor, "CLP");
    },
    [pais, formatAmount]
  );

  const mesesDelPeriodo = useMemo(() => getMesesDelPeriodo(periodo), [periodo]);

  const { filasEvolucion, filasCobertura } = useMemo(
    () =>
      construirDatosCategorias({
        clientes: data.clientes,
        oportunidades: data.oportunidades,
        categorias: data.categorias,
        empresasVipSet,
        clienteIdsConActividadReciente,
        mesesDelPeriodo,
        getImporteLocal,
      }),
    [data, empresasVipSet, clienteIdsConActividadReciente, mesesDelPeriodo, getImporteLocal]
  );

  const maximoCelda = useMemo(
    () => filasEvolucion.reduce((max, fila) => Math.max(max, ...fila.celdas.map((c) => c.monto)), 0),
    [filasEvolucion]
  );

  const abrirTramo = (categoriaLabel, estado, nombres) => setModalTramo({ categoriaLabel, estado, nombres });

  // Filtros del header — un único control real (selector de período). CALCADO byte a byte del
  // molde de VentasDetalle (.filtersContainer/.filterRow/select.form-control, 2026-09-23 — antes
  // tenía un flex-basis y un font-weight propios, "inventados"; ahora es EXACTAMENTE la misma
  // regla que usan las demás tabs, publicada igual por el contexto del header).
  const filtersContent = (
    <div className={styles.filtersContainer}>
      <div className={styles.filterRow}>
        <div>
          <select className="form-control" value={periodo} onChange={(e) => setPeriodo(e.target.value)}>
            <option value={PERIODOS.ANO_ACTUAL}>Año en curso</option>
            <option value={PERIODOS.ULTIMOS_12}>Últimos 12 meses</option>
          </select>
        </div>
      </div>
    </div>
  );

  useHubHeaderFilters(tabKey, filtersContent);
  const headerElement = tabKey ? null : renderHeader ? (
    renderHeader(filtersContent)
  ) : (
    <Header expandChildren>{filtersContent}</Header>
  );

  return (
    <div style={{ padding: tabKey ? 0 : 15 }}>
      {headerElement}
      <div className={styles.wrapper} style={{ minHeight: tabKey ? 0 : "100vh" }}>
        {loading ? (
          <div style={{ minHeight: 300 }}>
            <Loader />
          </div>
        ) : (
          <>
            {/* ── Panel de evolución ─────────────────────────────────────────────────────────── */}
            <div className={styles.panel}>
              <h3 className={styles.panelTitulo}>Evolución</h3>
              <div className={styles.tablaEvolucion}>
                <div className={`${styles.filaEvolucion} ${styles.filaHeader}`}>
                  <div className={styles.celdaLabel} />
                  <div className={styles.mesesGrid}>
                    {mesesDelPeriodo.map((mes) => (
                      <div key={mes} className={styles.celdaMesHeader}>
                        {etiquetaMesCorta(mes)}
                      </div>
                    ))}
                  </div>
                  <div className={styles.metricas}>
                    <div className={styles.celdaMetricaHeader}>Total</div>
                    <div className={styles.celdaMetricaHeader}>Empresas</div>
                    <div className={styles.celdaMetricaHeader}>Meses activos</div>
                    <div className={styles.celdaMetricaHeader}>Concentración</div>
                  </div>
                </div>
                {filasEvolucion.map((fila) => (
                  <div key={fila.filaKey} className={styles.filaEvolucion}>
                    <div className={styles.celdaLabel}>{fila.label}</div>
                    <div className={styles.mesesGrid}>
                      {fila.celdas.map((c) => (
                        <div
                          key={c.mes}
                          className={styles.celdaMes}
                          style={{ background: colorDeCelda(c.monto, maximoCelda) }}
                          title={`${fila.label} · ${etiquetaMes(c.mes)} · ${formatMonedaLocal(c.monto)}`}
                        />
                      ))}
                    </div>
                    <div className={styles.metricas}>
                      <div className={styles.celdaMetrica}>{formatMonedaLocal(fila.totalPeriodo)}</div>
                      <div className={styles.celdaMetrica}>{fila.empresasDistintas}</div>
                      <div className={styles.celdaMetrica}>{fila.mesesActivos}/12</div>
                      <div className={styles.celdaMetrica} title={fila.empresaConcentracion || ""}>
                        {fila.concentracionPct > 0 ? `${fila.concentracionPct.toFixed(0)}%` : "-"}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className={styles.leyenda}>
                <span className={styles.leyendaEtiqueta}>Menor</span>
                <span className={styles.leyendaSwatch} style={{ background: COLOR_VACIO, borderColor: "#dee2e6" }} />
                {ESCALA_VERDE.map((color) => (
                  <span key={color} className={styles.leyendaSwatch} style={{ background: color }} />
                ))}
                <span className={styles.leyendaEtiqueta}>Mayor</span>
              </div>

              <p className={styles.notaChica}>
                El total de empresas del mapa NO es la suma de las categorías: una empresa con marcas en dos categorías
                aparece en las dos, y una empresa VIP cuenta también en su categoría propia si es una de las 7.
              </p>
            </div>

            {/* ── Panel de cobertura ─────────────────────────────────────────────────────────── */}
            <div className={styles.panel}>
              <h3 className={styles.panelTitulo}>Cobertura</h3>
              <div className={styles.tablaCobertura}>
                {filasCobertura.map((fila) => (
                  <div key={fila.filaKey} className={styles.filaCobertura}>
                    <div className={styles.celdaLabel}>{fila.label}</div>
                    <div className={styles.coberturaPct}>{fila.activadoPct.toFixed(0)}%</div>
                    <div className={styles.coberturaTotal}>{fila.total} empresas</div>
                    <div className={styles.barraCobertura}>
                      {fila.activado > 0 && (
                        <button
                          type="button"
                          className={styles.tramoBarra}
                          style={{ width: `${(fila.activado / fila.total) * 100 || 0}%`, background: "#198754" }}
                          onClick={() => abrirTramo(fila.label, "activado", fila.nombresPorEstado.activado)}
                          title={`Activado: ${fila.activado}`}
                        >
                          {fila.activado / fila.total >= 0.08
                            ? `${Math.round((fila.activado / fila.total) * 100)}%`
                            : ""}
                        </button>
                      )}
                      {fila.vinculado > 0 && (
                        <button
                          type="button"
                          className={styles.tramoBarra}
                          style={{ width: `${(fila.vinculado / fila.total) * 100 || 0}%`, background: "#adb5bd" }}
                          onClick={() => abrirTramo(fila.label, "vinculado", fila.nombresPorEstado.vinculado)}
                          title={`Vinculado: ${fila.vinculado}`}
                        >
                          {fila.vinculado / fila.total >= 0.08
                            ? `${Math.round((fila.vinculado / fila.total) * 100)}%`
                            : ""}
                        </button>
                      )}
                      {fila.sinContacto > 0 && (
                        <button
                          type="button"
                          className={styles.tramoBarra}
                          style={{
                            width: `${(fila.sinContacto / fila.total) * 100 || 0}%`,
                            background: "#f1f3f5",
                            color: "#495057",
                          }}
                          onClick={() => abrirTramo(fila.label, "sin_contacto", fila.nombresPorEstado.sin_contacto)}
                          title={`Sin contacto: ${fila.sinContacto}`}
                        >
                          {fila.sinContacto / fila.total >= 0.08
                            ? `${Math.round((fila.sinContacto / fila.total) * 100)}%`
                            : ""}
                        </button>
                      )}
                      {fila.total === 0 && <div className={styles.tramoVacio}>Sin empresas</div>}
                    </div>
                  </div>
                ))}
              </div>
              <div className={styles.leyenda}>
                <span className={styles.leyendaSwatch} style={{ background: "#198754" }} />
                <span className={styles.leyendaEtiqueta}>Activado</span>
                <span className={styles.leyendaSwatch} style={{ background: "#adb5bd" }} />
                <span className={styles.leyendaEtiqueta}>Vinculado</span>
                <span className={styles.leyendaSwatch} style={{ background: "#f1f3f5", borderColor: "#dee2e6" }} />
                <span className={styles.leyendaEtiqueta}>Sin contacto</span>
              </div>
            </div>
          </>
        )}
      </div>

      {modalTramo && (
        <ListaEmpresasModal
          show={!!modalTramo}
          onHide={() => setModalTramo(null)}
          categoriaLabel={modalTramo.categoriaLabel}
          estado={modalTramo.estado}
          nombres={modalTramo.nombres}
        />
      )}
    </div>
  );
};

export default Categorias;
